import { workoutMetricLabel } from '../../utils/workoutMetricLabel';
import { workoutDisplayDigits as displayDigits } from '../../utils/workoutNumberFormat';
import { isRunningWorkout, formatRunningPace, type RunningMetrics } from '../../utils/runningWorkoutMetrics';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { WorkoutDetail } from '../../services/api';
import { secToHMS } from '../../utils/secToHMS';

type LapRecord = Record<string, unknown>;

interface ColDef {
  key: string;
  label: string;
  fields: string[];
  fmt?: (v: unknown) => string;
}

function lapValue(lap: LapRecord, fields: string[]): unknown {
  for (const field of fields) {
    const value = lap[field];
    if (value != null && value !== '') return value;
  }
  return null;
}

function formatLapCell(col: ColDef, lap: LapRecord): string {
  const value = lapValue(lap, col.fields);
  if (value == null) return '—';
  return col.fmt ? col.fmt(value) : String(value);
}

/** Lap table for workout summary (inline, not a top-level tab). */
export function MobileWorkoutLapsTab({ data }: { data: WorkoutDetail }) {
  const { t } = useTranslation();
  const laps = ((data.details?.lap_data ?? []) as LapRecord[]).map(lap => {
    const running = lap.running_metrics as RunningMetrics | undefined;
    return isRunningWorkout(data) ? { ...lap, avg_cadence: running?.cadence?.avg ?? null, running_pace: running?.avg_pace_seconds_per_km, rtss: running?.rtss } : lap;
  });

  const cols = useMemo(() => {
    const allCols: ColDef[] = [
      { key: 'lap', label: '#', fields: ['lap'] },
      {
        key: 'time',
        label: t('workoutDetail.summary.lapTime'),
        fields: ['total_elapsed_time', 'elapsed_time', 'total_timer_time'],
        fmt: (v) => secToHMS(Number(v)),
      },
      { key: 'distance', label: 'm', fields: ['total_distance', 'distance'], fmt: (v) => String(Math.round(Number(v))) },
      { key: 'heartRate', label: 'HR', fields: ['avg_heart_rate', 'average_heart_rate', 'heart_rate'], fmt: (v) => String(Math.round(Number(v))) },
      { key: 'power', label: 'W', fields: ['avg_power', 'average_power', 'power'], fmt: (v) => String(Math.round(Number(v))) },
      { key: 'speed', label: 'km/h', fields: ['avg_speed', 'average_speed', 'speed'], fmt: (v) => Number(v).toFixed(1) },
      { key: 'cadence', label: isRunningWorkout(data) ? `${t('runningMetrics.cadence')} (${t('runningMetrics.cadenceUnit')})` : 'rpm', fields: ['avg_cadence', 'average_cadence', 'cadence'], fmt: (v) => String(Math.round(Number(v))) },
      { key: 'tss', label: 'TSS', fields: ['tss_actual', 'tss'] },
    ];
    return allCols.filter(
      (c) => c.key === 'lap' || laps.some((lap) => lapValue(lap, c.fields) != null),
    );
  }, [laps, t, data]);

  if (!laps.length) return null;

  return (
    <div className="ws-section">
      <div className="ws-laps-table-wrap">
        <table className="ws-laps-table">
          <thead>
            <tr>
              {cols.map((c) => (
                <th key={c.key} className="ws-laps-table__th">
                  {workoutMetricLabel(c.label)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {laps.map((lap, i) => (
              <tr key={i} className={i % 2 ? 'ws-laps-table__row--alt' : undefined}>
                {cols.map((c) => (
                  <td key={c.key} className="ws-laps-table__td ltr-island">
                    {displayDigits(c.key === 'lap'
                      ? String(Number(lap.lap ?? i) + 1)
                      : formatLapCell(c, lap))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
